import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { toast } from "@/lib/toast";
import { apiErrorMessage } from "@/lib/api";
import { validateLoginIdentifier } from "@/lib/loginIdentifier";
import { isCentralLoginHost, validateWorkspaceUrl } from "@/lib/workspaceUrl";
import { useAppStore } from "@/store/useAppStore";
import { useOnboardingStore } from "@/store/useOnboardingStore";
import { LoginCredentialsForm, LoginMfaForm } from "@/pages/auth/LoginFormSections";

function loginRedirectDestination(returnTo, onboardingCompleted, currentStep = 1) {
  if (returnTo && returnTo !== "/login") return returnTo;
  if (onboardingCompleted) return "/today";
  const step = Math.max(1, Number(currentStep) || 1);
  return `/onboarding/${step}`;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, completeMfa } = useAppStore();
  const onboardingCompletedStore = useOnboardingStore((s) => s.completed);
  const currentStep = useOnboardingStore((s) => s.currentStep);

  const returnTo = location.state?.from?.pathname;
  const finishLogin = (onboardingCompleted = onboardingCompletedStore) => {
    const dest = loginRedirectDestination(
      returnTo,
      onboardingCompleted,
      useOnboardingStore.getState().currentStep || currentStep,
    );
    setTimeout(() => navigate(dest, { replace: true }), 50);
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [workspaceUrl, setWorkspaceUrl] = useState("");
  const [workspaceUrlError, setWorkspaceUrlError] = useState("");
  const centralLogin = isCentralLoginHost();
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(null);
  const [mfaState, setMfaState] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState("");
  const [pendingDel, setPendingDel] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  const focusEmailField = () => {
    requestAnimationFrame(() => {
      document.getElementById("email")?.focus();
    });
  };

  const handleSignInResult = (r) => {
    if (r?.mfaRequired) {
      setMfaState({ challengeId: r.challengeId });
      return true;
    }
    finishLogin(r?.onboardingCompleted ?? onboardingCompletedStore);
    return false;
  };

  const showEmailError = (msg) => {
    setEmailError(msg);
    focusEmailField();
    return toast.error(msg);
  };

  const validatedLoginEmail = () => {
    if (centralLogin) {
      const ws = validateWorkspaceUrl(workspaceUrl);
      if (!ws.ok) {
        setWorkspaceUrlError(ws.message);
        toast.error(ws.message);
        return null;
      }
      setWorkspaceUrlError("");
    }
    const id = validateLoginIdentifier(email);
    if (!id.ok) {
      showEmailError(id.message);
      return null;
    }
    setEmailError("");
    if (!password) {
      toast.error("Please enter your password");
      return null;
    }
    return id.value;
  };

  const handleSignInError = (err) => {
    if (err?.code === "account_pending_deletion") {
      const when = err?.data?.detail?.error?.deletion_scheduled_at;
      setPendingDel({ scheduled_at: when });
      return;
    }
    const isEmailValidation = err?.status === 422
      && /email/i.test(String(err?.message || "") + JSON.stringify(err?.data?.detail || ""));
    if (isEmailValidation) {
      showEmailError("Please enter a valid email address.");
      return;
    }
    const msg = err?.code === "invalid_credentials"
      ? "Invalid email or password. Check your credentials or contact your administrator."
      : apiErrorMessage(err, "Sign-in failed");
    toast.error(msg, {
      description: err?.code === "invalid_credentials"
        ? "Check your credentials or use Forgot password to reset."
        : undefined,
      duration: 5000,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const trimmedEmail = validatedLoginEmail();
    if (!trimmedEmail) return;
    setLoading(true);
    try {
      const r = await signIn(trimmedEmail, password);
      handleSignInResult(r);
    } catch (err) {
      handleSignInError(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelDeletion = async () => {
    setCancelBusy(true);
    try {
      const { authApi } = await import("@/lib/api");
      await authApi.cancelDeletion(email, password);
      toast.success("Deletion canceled — signing you in…");
      setPendingDel(null);
      const r = await signIn(email, password);
      handleSignInResult(r);
    } catch (err) {
      toast.error(err?.message || "Could not cancel deletion");
    } finally {
      setCancelBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (!mfaCode) return toast.error("Enter your 6-digit code");
    setMfaError("");
    setLoading(true);
    try {
      const r = await completeMfa(mfaState.challengeId, mfaCode);
      finishLogin(r?.onboardingCompleted ?? onboardingCompletedStore);
    } catch (err) {
      setLoading(false);
      const msg = err?.message || "Invalid code. Please try again.";
      setMfaError(msg);
      toast.error(msg);
    }
  };

  const sso = (provider) => {
    setSsoLoading(provider);
    toast("SSO is mocked for Phase A — use email + password");
    setTimeout(() => setSsoLoading(null), 700);
  };

  return (
    <AuthLayout eyebrow="Welcome back" headline="Sign in to Sellyx" sub="Pipeline, inbox, agents and signals — all in one cockpit.">
      {mfaState ? (
        <LoginMfaForm
          mfaCode={mfaCode}
          mfaError={mfaError}
          loading={loading}
          onCodeChange={(e) => {
            setMfaCode(e.target.value.replace(/\D/g, ""));
            if (mfaError) setMfaError("");
          }}
          onSubmit={submitMfa}
          onBack={() => {
            setMfaState(null);
            setMfaCode("");
            setPassword("");
            setMfaError("");
          }}
        />
      ) : (
        <LoginCredentialsForm
          pendingDel={pendingDel}
          cancelBusy={cancelBusy}
          centralLogin={centralLogin}
          workspaceUrl={workspaceUrl}
          workspaceUrlError={workspaceUrlError}
          email={email}
          emailError={emailError}
          password={password}
          loading={loading}
          ssoLoading={ssoLoading}
          onWorkspaceUrlChange={(e) => {
            setWorkspaceUrl(e.target.value);
            if (workspaceUrlError) setWorkspaceUrlError("");
          }}
          onEmailChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError("");
          }}
          onPasswordChange={(e) => setPassword(e.target.value)}
          onSubmit={submit}
          onCancelDeletion={cancelDeletion}
          onDismissPendingDeletion={() => setPendingDel(null)}
          onSso={sso}
        />
      )}
    </AuthLayout>
  );
}
